






import { Component, Prop, Vue, Watch } from 'vue-property-decorator'

@Component({})
export default class Teleport extends Vue {
  @Prop({type: String, required: true}) private to!: string;
  @Prop({type: String, default: 'after'}) private where!: string;
  @Prop({type: Boolean}) private disabled!: string;

  nodes: any[] = [];
  waiting = false;
  observer: MutationObserver | undefined = undefined;
  childObserver: MutationObserver | undefined = undefined;
  parent: Element | undefined = undefined;
  moved = false;
  
  get classes(): string[] {
    if (this.disabled) return ['teleporter']
    return ['teleporter', 'hidden']
  }
  
  mounted(): void {
    this.nodes = Array.from(this.$el.childNodes);
    
    if (!this.disabled) {
      this.bootObserver();
    }
  }
  
  beforeDestroy(): void {
    // Fix nodes reference
    this.nodes = this.getComponentChildrenNode();
    
    // Move back
    this.disable();
    
    // stop observing
    this.teardownObserver();
  }

  maybeMove(): void {
    if (!this.disabled) {
      this.move();
    }
  }

  async move(): Promise<void> {
    await this.$nextTick();
    this.waiting = false;

    const parent = document.querySelector(this.to);

    if (!parent) {
      this.disable();

      this.waiting = true;

      return;
    }

    this.parent = parent;

    if (this.where === 'before') {
      this.parent.prepend(this.getFragment());
    } else {
      this.parent.appendChild(this.getFragment());
    }
  }

  disable(): void {
    if (this.parent) {
      this.$el.appendChild(this.getFragment());
      this.parent = undefined;
    }
  }
  // Using a fragment is faster because it'll trigger only a single reflow
  // See https://developer.mozilla.org/en-US/docs/Web/API/DocumentFragment
  getFragment(): DocumentFragment {
    const fragment = document.createDocumentFragment();

    this.nodes.forEach(node => fragment.appendChild(node));

    return fragment;
  }

  onMutations(mutations: MutationRecord[]): void {
    // Makes sure the move operation is only done once
    let shouldMove = false;

    for (let i = 0; i < mutations.length; i++) {
      const mutation = mutations[i];
      const filteredAddedNodes = Array.from(mutation.addedNodes).filter(node => !this.nodes.includes(node));

      if (Array.from(mutation.removedNodes).includes(this.parent as any)) {
        this.disable();
        this.waiting = !this.disabled;
      } else if (this.waiting && filteredAddedNodes.length > 0) {
        shouldMove = true;
      }
    }

    if (shouldMove) {
      this.move();
    }
  }
  
  async bootObserver(): Promise<void> {
    await this.$nextTick();
    if (this.observer) {
      return;
    }

    this.observer = new MutationObserver(mutations => this.onMutations(mutations));

    this.observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: false,
      characterData: false,
    });

    if (this.childObserver) {
      return;
    }
    // watch childNodes change
    this.childObserver = new MutationObserver(mutations => {
      const childChangeRecord = mutations.find(i => i.target === this.$el);
      if (childChangeRecord) {
        // Remove old nodes before update position.
        this.nodes.forEach((node) => node.parentNode && node.parentNode.removeChild(node));
        this.nodes = this.getComponentChildrenNode();
        this.maybeMove();
      }
    });

    this.childObserver.observe(this.$el, {
      childList: true,
      subtree: false,
      attributes: false,
      characterData: false,
    });
  }

  teardownObserver(): void {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = undefined;
    }
    if (this.childObserver) {
      this.childObserver.disconnect();
      this.childObserver = undefined;
    }
  }

  getComponentChildrenNode(): Node[] {
    return this.$vnode?.componentOptions?.children
      ?.map((i) => i.elm)
      .filter((i) => i) as Node[];
  }
  
  @Watch('to', {immediate: true})
  onToPropChanged(): void {
    this.maybeMove();
  }
  
  @Watch("where", {immediate: true})
  onWherePropChanged(): void {
    this.maybeMove();
  }
  
  @Watch("disabled", {immediate: true})
  onDisabledPropChanged(bool: boolean): void {
    if (bool) { 
      this.disable()
      
      this.$nextTick(() => {
        this.teardownObserver()
      })
    } else {
      this.bootObserver();
      this.move();
    }
  }
}

